import React, { useContext } from 'react'
import moment from 'moment'
import StatusCard from '../../../../components/Cards/StatusCard'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { PayrollContext } from './Payroll'
import { TenantSettingsContext } from '../../../../App'
import { formatMoney } from '../../../../helpers/currency'
import { useNavigate } from 'react-router-dom'

const colors = (color) => {
  switch (color) {
    case 1:
      return {
        main: 'hsla(343,85%,63%, 1)',
        light: 'hsla(343, 85%, 73%, 1)',
        dark: 'hsl(343, 85%, 53%)',
      }
    case 2:
      return {
        main: 'hsla(242, 30%, 56%, 1)',
        light: 'hsla(242, 30%, 66%, 1)',
        dark: 'hsla(242, 30%, 46%, 1)',
      }
    case 3:
      return {
        main: 'hsla(196, 73%, 54%, 1)',
        light: 'hsla(196, 73%, 64%, 1)',
        dark: 'hsla(196, 73%, 44%, 1)',
      }
    case 4:
      return {
        main: 'hsla(181, 55%, 49%, 1)',
        light: 'hsla(181, 55%, 59%, 1)',
        dark: 'hsla(181, 55%, 39%, 1)',
      }
    case 5:
      return {
        main: 'hsla(206, 55%, 11%, 1)',
        light: 'hsla(206, 55%, 21%, 1)',
        dark: 'hsla(206, 55%, 1%, 1)',
      }
    case 6:
      return {
        main: 'hsla(339, 62%, 30%, 1)',
        light: 'hsla(339, 62%, 40%, 1)',
        dark: 'hsla(339, 62%, 20%, 1)',
      }
    case 7:
      return {
        main: 'hsla(167, 92%, 15%, 1)',
        light: 'hsla(167, 92%, 25%, 1)',
        dark: 'hsla(167, 92%, 5%, 1)',
      }
    case 8:
      return {
        main: 'hsla(223, 53%, 31%, 1)',
        light: 'hsla(223, 53%, 41%, 1)',
        dark: 'hsla(223, 53%, 21%, 1)',
      }
    case 9:
      return {
        main: 'hsla(175, 75%, 21%, 1)',
        light: 'hsla(175, 75%, 31%, 1)',
        dark: 'hsla(175, 75%, 11%, 1)',
      }
    case 10:
      return {
        main: 'hsla(343, 91%, 52%, 1)',
        light: 'hsla(343, 91%, 62%, 1)',
        dark: 'hsla(343, 91%, 42%, 1)',
      }
    default:
      return {
        main: 'hsla(150, 1%, 34%, 1)',
        light: 'hsla(150, 1%, 44%, 1)',
        dark: 'hsla(150, 1%, 24%, 1)',
      }
  }
}

const TopPanel = ({}) => {
  const {
    wagesTotals: totals,
    searchParams,
    setSearchParams,
  } = useContext(PayrollContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const currency = (value) => formatMoney(value, orgSettings?.currency)
  const navigate = useNavigate()
  const TopCard = ({ title, caption, color, style, onClick, active }) => {
    return (
      <Grid item xs={12} md={6} lg={3}>
        <Stack height="100%">
          <Stack
            gap={1}
            color={active ? 'contrast.main' : 'white'}
            boxShadow={2}
            borderRadius={2}
            bgcolor={active ? 'contrast.main' : 'secondary.light'}
            flexGrow={1}
            padding={1}
            alignItems="center"
            sx={{
              cursor: 'pointer',
              transition: 'all .2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 12px 24px rgba(0,0,0,.18)',
              },
            }}
            onClick={onClick}
          >
            <Typography variant="title2" color="white">
              {title}
            </Typography>
            <Typography variant="caption" color="white">
              {caption}
            </Typography>
          </Stack>
        </Stack>
      </Grid>
    )
  }
  const handleClick = ({ startDate, endDate }) => {
    setSearchParams((prev) => ({
      ...prev,
      startDate,
      endDate,
      paid: false,
      employee: false,
    }))
  }

  const dateSetters = {
    lastMonth: {
      startDate: moment().subtract(1, 'month').startOf('month'),
      endDate: moment().subtract(1, 'month').endOf('month'),
    },
    thisMonth: {
      startDate: moment().startOf('month'),
      endDate: moment().endOf('day'),
    },
    all: {
      startDate: moment(totals?.earliestStart),
      endDate: moment(totals?.latestStart),
    },
  }

  return (
    <Grid container spacing={1}>
      <TopCard
        title={currency(totals?.lastMonthsWages ?? 0)}
        caption={`Unpaid Wages ${moment().subtract(1, 'month').format('MMMM')}`}
        color={9}
        onClick={() => {
          setSearchParams((prev) => ({
            ...prev,
            ...dateSetters.lastMonth,
          }))
        }}
        active={
          moment(searchParams.startDate).isSame(
            dateSetters.lastMonth.startDate,
          ) &&
          moment(searchParams.endDate).isSame(dateSetters.lastMonth.endDate)
        }
      />
      <TopCard
        title={currency(totals?.thisMonthsWages ?? 0)}
        caption={`Unpaid Wages ${moment().format('MMMM')}`}
        color={7}
        onClick={() => {
          setSearchParams((prev) => ({
            ...prev,
            ...dateSetters.thisMonth,
          }))
        }}
        active={
          moment(searchParams.startDate).isSame(
            dateSetters.thisMonth.startDate,
          ) &&
          moment(searchParams.endDate).isSame(dateSetters.thisMonth.endDate)
        }
      />
      <TopCard
        title={currency(totals?.totalUnpaidWages ?? 0)}
        caption="Total Outstanding"
        color={8}
        onClick={() => {
          setSearchParams((prev) => ({
            ...prev,
            ...dateSetters.all,
          }))
        }}
        active={
          moment(searchParams.startDate).isSame(dateSetters.all.startDate) &&
          moment(searchParams.endDate).isSame(dateSetters.all.endDate)
        }
      />
      <TopCard
        title={totals?.pendingOverTimeClaims ?? 0}
        caption="Pending Overtime Claims"
        color={6}
        onClick={() => {
          navigate(`/overtime-claims`)
        }}
        active={totals?.pendingOverTimeClaims}
      />
    </Grid>
  )
}

export default TopPanel
